<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template #header>
      <div class="p-dialog-title">
        {{ isNew ? $t('tenantApplicationCreateModalTitle') : $t('tenantApplicationEditModalTitle') }}
        <span v-if="form.id">#{{ form.id }}</span>
      </div>
    </template>

    <Spinner v-if="dataIsSending"/>

    <!--    <div v-else class="p-formgrid p-grid p-pt-3">-->
    <div v-else class="p-formgrid p-grid">
      <div class="p-col-12 p-mb-5">
        <div class="p-text-bold">{{ $t('Company') }}</div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="tenant-application-company-name"
                     v-model.trim="form.company_name"
                     :disabled="dataIsSending"
                     :class="{ 'p-invalid': fieldInvalid('company_name') }"
                     maxlength="100"
                     autocomplete="off"/>
          <label for="tenant-application-company-name">
            {{ $t('Company name') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small v-if="fieldError('company_name')" class="p-invalid">
          {{ fieldError('company_name') }}
        </small>
      </div>

<!--      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">-->
<!--        <div class="p-float-label">-->
<!--          <CustomDropdown id="tenant-application-country"-->
<!--                          v-model="form.country_code"-->
<!--                          :disabled="dataIsSending"-->
<!--                          :options="countryOptions"-->
<!--                          optionLabel="label"-->
<!--                          optionValue="value"-->
<!--                          :filter="true"-->
<!--                          :showClear="false"-->
<!--                          :class="{ 'p-invalid': fieldInvalid('country_code') }"-->
<!--                          @change="onCompanyCountryChange"/>-->
<!--          <label for="tenant-application-country">-->
<!--            {{ $t('Country') }}<span class="warning-color">*</span>-->
<!--          </label>-->
<!--        </div>-->
<!--        <small v-if="fieldError('country_code')" class="p-invalid">-->
<!--          {{ fieldError('country_code') }}-->
<!--        </small>-->
<!--      </div>-->

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <CountryDropdown
            :model-value="selectedCountry"
            :country-code="form.country_code"
            :submitted="submitted"
            :required="true"
            :invalid="!!serverFieldError('country_code')"
            :disabled="dataIsSending"
            :visible="visible"
            :show-clear="false"
            input-id="tenant-application-country"
            label="Country"
            @update:model-value="updateCountry"
            @change="onCompanyCountryChange"/>
        <small v-if="serverFieldError('country_code')" class="p-invalid">
          {{ serverFieldError('country_code') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="tenant-application-registry-code"
                     v-model.trim="form.registry_code"
                     :disabled="dataIsSending"
                     :class="{ 'p-invalid': fieldInvalid('registry_code') }"
                     maxlength="100"
                     autocomplete="off"/>
          <label for="tenant-application-registry-code">
            {{ $t('registrationRegistryCodeLabel') }}
            <span v-if="registryCodeRequired" class="warning-color">*</span>
          </label>
        </div>
        <small v-if="fieldError('registry_code')" class="p-invalid">
          {{ fieldError('registry_code') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="tenant-application-website"
                     v-model.trim="form.website"
                     :disabled="dataIsSending"
                     :class="{ 'p-invalid': fieldInvalid('website') }"
                     maxlength="255"
                     autocomplete="off"/>
          <label for="tenant-application-website">{{ $t('Website') }}</label>
        </div>
        <small v-if="fieldError('website')" class="p-invalid">
          {{ fieldError('website') }}
        </small>
      </div>

      <div class="p-col-12 p-mb-5">
        <div class="p-text-bold">{{ $t('Contact person') }}</div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="tenant-application-first-name"
                     v-model.trim="form.applicant_first_name"
                     @input="onApplicantNameInput('applicant_first_name', $event)"
                     @keydown="onNameKeyDown"
                     @paste="onNamePaste"
                     :disabled="dataIsSending"
                     :class="{ 'p-invalid': fieldInvalid('applicant_first_name') }"
                     maxlength="100"
                     autocomplete="off"/>
          <label for="tenant-application-first-name">
            {{ $t('First name') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small v-if="fieldError('applicant_first_name')" class="p-invalid">
          {{ fieldError('applicant_first_name') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="tenant-application-last-name"
                     v-model.trim="form.applicant_last_name"
                     @input="onApplicantNameInput('applicant_last_name', $event)"
                     @keydown="onNameKeyDown"
                     @paste="onNamePaste"
                     :disabled="dataIsSending"
                     :class="{ 'p-invalid': fieldInvalid('applicant_last_name') }"
                     maxlength="100"
                     autocomplete="off"/>
          <label for="tenant-application-last-name">
            {{ $t('Last name') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small v-if="fieldError('applicant_last_name')" class="p-invalid">
          {{ fieldError('applicant_last_name') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <EmailInput v-model="form.email"
                    v-model:isInvalid="emailInvalid"
                    :submitted="submitted"
                    :required="true"
                    inputId="tenant-application-email"/>
        <small v-if="serverFieldError('email')" class="p-invalid">
          {{ serverFieldError('email') }}
        </small>
        <small v-if="form.email_verified_at" class="p-text-secondary">
          {{ $t('tenantApplicationEmailChangeRequiresVerification') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <PhoneInput v-model:phoneCountry="form.phone_country"
                    v-model:phoneCode="form.phone_code"
                    v-model:phoneNumber="form.phone_number"
                    v-model:isInvalid="phoneInvalid"
                    :required="true"
                    :disabled="dataIsSending"
                    :visible="visible"
                    :modalData="form"
                    :submitted="submitted"
                    name="tenant-application"/>
        <small v-if="phoneServerError" class="p-invalid">
          {{ phoneServerError }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="tenant-application-role"
                          v-model="form.applicant_role"
                          :disabled="dataIsSending"
                          :options="applicantRoleOptions"
                          optionLabel="label"
                          optionValue="value"
                          :showClear="false"
                          :class="{ 'p-invalid': fieldInvalid('applicant_role') }"/>
          <label for="tenant-application-role">
            {{ $t('registrationRoleLabel') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small v-if="fieldError('applicant_role')" class="p-invalid">
          {{ fieldError('applicant_role') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="tenant-application-language"
                          v-model="form.language"
                          :disabled="dataIsSending"
                          :options="languageOptions"
                          optionLabel="label"
                          optionValue="value"
                          :showClear="false"
                          :class="{ 'p-invalid': fieldInvalid('language') }"/>
          <label for="tenant-application-language">
            {{ $t('Language') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small v-if="fieldError('language')" class="p-invalid">
          {{ fieldError('language') }}
        </small>
      </div>

      <div class="p-field p-col-12">
        <LimitedCharsTextarea :submitted="submitted"
                              :rows="2"
                              v-model="form.comment"
                              :maxChars="commentMaxChars"
                              id="tenant-application-modal-notes"
                              :label="$t('Notes')"/>
      </div>

      <div v-if="generalServerError" class="p-col-12">
        <div class="warning-background p-p-3">
          {{ generalServerError }}
        </div>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              :disabled="dataIsSending"
              @click="close"/>
      <Button :label="$t('Save')"
              icon="pi pi-check"
              class="p-button-text"
              :loading="dataIsSending"
              :disabled="dataIsSending"
              @click="saveItem"/>
    </template>
  </Modal>
</template>


<script>
import httpClient from '@/services/http.services'
import settings from '@/settings'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import inputsFormatMixins from '@/mixins/inputsFormatMixins'
import PhoneInput from '@/components/PhoneInput'
import EmailInput from '@/components/EmailInput'
import CountryDropdown from '@/components/CountryDropdown.vue'

export default {
  name: 'TenantApplicationModal',


  emits: ['close', 'update-items'],
  mixins: [
    showErrorsMixins,
    inputsFormatMixins,
  ],

  components: {
    PhoneInput,
    EmailInput,
    CountryDropdown,
  },

  props: {
    item: {
      type: Object,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedCountry: null,
      dataIsSending: false,
      submitted: false,
      phoneInvalid: false,
      emailInvalid: false,
      serverErrors: {},
      generalServerError: null,
      commentMaxChars: 512,
      form: this.emptyForm(),
      settings,
    }
  },

  watch: {
    item: {
      immediate: true,
      handler(value) {
        if (value) {
          this.createItemData(value)
        }
      },
    },

    visible(value) {
      if (value) {
        this.createItemData(this.item)
      } else {
        this.resetModal()
      }
    },
  },

  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {
          position: 'absolute',
          top: 0,
          width: '95%',
          maxWidth: '600px',
          marginTop: '10px',
        }
      }

      if (this.$store.state.tabletLayout) {
        return {
          position: 'absolute',
          top: 0,
          width: '600px',
          maxWidth: '95%',
          marginTop: '15px',
        }
      }

      return {
        position: 'absolute',
        top: 0,
        width: '600px',
      }
    },

    isNew() {
      return !this.form.id
    },

    languageOptions() {
      return (settings.languages || [])
          .filter(language => settings.allowedLangs?.includes(language.value))
          .map(language => ({
            value: language.value,
            label: language.label,
          }))
    },

    applicantRoleOptions() {
      return [
        { value: 'owner', label: this.$t('Owner') },
        { value: 'board_member', label: this.$t('Board member') },
        { value: 'manager', label: this.$t('Manager') },
        { value: 'employee', label: this.$t('Employee') },
        { value: 'other', label: this.$t('other') },
      ]
    },

    registryCodeRequired() {
      return this.form.country_code === 'EE'
    },

    phoneServerError() {
      return this.serverFieldError('phone_number')
          || this.serverFieldError('phone_code')
          || this.serverFieldError('phone_country')
    },
  },

  methods: {
    emptyForm() {
      return {
        id: null,

        company_name: '',
        registry_code: '',
        website: '',
        country_code: 'EE',

        applicant_first_name: '',
        applicant_last_name: '',
        applicant_role: null,
        email: '',

        phone_country: 'ee',
        phone_code: '372',
        phone_number: '',

        language: settings.allowedLangs?.includes(this.$i18n?.locale)
            ? this.$i18n.locale
            : settings.fallbackLanguage,
        email_verified_at: null,
        comment: null,
      }
    },

    updateCountry(country) {
      this.selectedCountry = country

      this.form.country_code = country?.code ? String(country.code).toUpperCase() : null
    },

    createItemData(item) {
      if (!item) {
        this.form = this.emptyForm()
        return
      }

      this.form = {
        ...this.emptyForm(),
        id: item.id ?? null,

        company_name: item.company_name ?? '',
        registry_code: item.registry_code ?? '',
        website: item.website ?? '',
        country_code: String(item.country_code || 'EE').toUpperCase(),

        applicant_first_name: item.applicant_first_name ?? '',
        applicant_last_name: item.applicant_last_name ?? '',
        applicant_role: item.applicant_role ?? null,
        email: item.email ?? '',

        phone_country: item.phone_country ?? null,
        phone_code: item.phone_code ?? null,
        phone_number: item.phone_number ?? '',

        language: item.language ?? 'en',
        email_verified_at: item.email_verified_at ?? null,
        comment: item.comment ?? null,
      }

      this.submitted = false
      this.phoneInvalid = false
      this.emailInvalid = false
      this.serverErrors = {}
      this.generalServerError = null
    },

    resetModal() {
      this.dataIsSending = false
      this.submitted = false
      this.phoneInvalid = false
      this.emailInvalid = false
      this.serverErrors = {}
      this.generalServerError = null
      this.selectedCountry = null
      this.form = this.emptyForm()
    },

    onApplicantNameInput(field, event) {
      this.form[field] = this.formatName(event.target.value ?? '')
    },

    onCompanyCountryChange(country) {
      const countryCode = String(
          country?.code
          ?? this.form.country_code
          ?? ''
      ).toUpperCase()

      this.form.country_code = countryCode

      this.form.phone_country = countryCode ? countryCode.toLowerCase() : null

      this.form.phone_code = null
    },

    fieldInvalid(field) {
      return Boolean(this.fieldError(field))
    },

    fieldError(field) {
      const serverError = this.serverFieldError(field)
      if (serverError) return serverError

      if (!this.submitted) return null

      const requiredFields = [
        'company_name',
        'country_code',
        'applicant_first_name',
        'applicant_last_name',
        'applicant_role',
        'email',
        'language',
      ]

      if (
          requiredFields.includes(field)
          && !String(this.form[field] || '').trim()
      ) {
        return this.$t('Required field')
      }

      if (
          field === 'registry_code'
          && this.registryCodeRequired
          && !String(this.form.registry_code || '').trim()
      ) {
        return this.$t('Required field')
      }

      if (
          field === 'email'
          && this.form.email
          && !this.emailLooksValid(this.form.email)
      ) {
        return this.$t('Invalid email')
      }

      if (
          field === 'website'
          && this.form.website
          && !this.websiteLooksValid(this.form.website)
      ) {
        return this.$t('Invalid website')
      }

      return null
    },

    serverFieldError(field) {
      const value = this.serverErrors?.[field]

      if (Array.isArray(value)) {
        return value[0] || null
      }

      return typeof value === 'string'
          ? value
          : null
    },

    emailLooksValid(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          String(value || '').trim()
      )
    },

    websiteLooksValid(value) {
      const website = String(value || '').trim()

      if (!website) return true
      if (/\s/u.test(website)) return false

      try {
        const url = new URL(this.normalizeWebsite(website))
        const hostname = url.hostname.toLowerCase()
        const labels = hostname.split('.')

        return Boolean(
            ['http:', 'https:'].includes(url.protocol)
            && !url.username
            && !url.password
            && labels.length >= 2
            && labels.every(label =>
                /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label)
            )
        )
      } catch (e) {
        return false
      }
    },

    phoneLooksValid() {
      const number = String(this.form.phone_number || '')
      const code = String(this.form.phone_code || '')

      return Boolean(
          this.form.phone_country
          && code
          && /^\d+$/.test(code)
          && /^\d{5,20}$/.test(number)
      )
    },

    formLooksValid() {
      return Boolean(
          this.form.company_name
          && this.form.country_code
          && (!this.registryCodeRequired || this.form.registry_code)
          && this.form.applicant_first_name
          && this.form.applicant_last_name
          && this.form.applicant_role
          && this.form.email
          && this.form.language
          && this.emailLooksValid(this.form.email)
          && (!this.form.website || this.websiteLooksValid(this.form.website))
          && this.phoneLooksValid()
          && !this.phoneInvalid
          && !this.emailInvalid
      )
    },

    normalizeWebsite(value) {
      const website = String(value || '').trim()

      if (!website) return null

      return /^https?:\/\//i.test(website)
          ? website
          : `https://${website}`
    },

    buildPayload() {
      const payload = {
        company_name: String(this.form.company_name || '').trim(),
        registry_code: String(this.form.registry_code || '').trim() || null,
        website: this.normalizeWebsite(this.form.website),
        country_code: String(this.form.country_code || '').toUpperCase(),

        applicant_first_name: String(this.form.applicant_first_name || '').trim(),
        applicant_last_name: String(this.form.applicant_last_name || '').trim(),
        applicant_role: this.form.applicant_role,
        email: String(this.form.email || '').trim().toLowerCase(),

        phone_country: String(this.form.phone_country || '').toLowerCase(),
        phone_code: String(this.form.phone_code || '').replace(/\D/g, ''),
        phone_number: String(this.form.phone_number || '').replace(/\D/g, ''),

        language: this.form.language || 'en',
        comment: String(this.form.comment || '').trim() || null,
      }

      if (!this.isNew) {
        payload.id = +this.form.id
      }

      return payload
    },

    async saveItem() {
      if (this.dataIsSending) return

      this.submitted = true
      this.serverErrors = {}
      this.generalServerError = null

      if (!this.formLooksValid()) {
        return
      }

      this.dataIsSending = true

      try {
        const { status, data } = await httpClient.post(
            this.isNew
                ? 'tenant-application/create-manual'
                : 'tenant-application/update',
            this.buildPayload()
        )

        if ([200, 201].includes(status) && data?.success) {
          this.$emit('update-items', data)

          this.$toast.add({
            severity: 'success',
            detail: this.$t(this.isNew ? 'Data saved' : 'Data updated'),
            life: settings.toastLife,
          })

          this.close()
          return
        }

        this.applyServerErrors(data)
      } catch (error) {
        const responseData = error?.response?.data

        if (responseData) {
          this.applyServerErrors(responseData)
        } else {
          this.showError(error)
        }
      } finally {
        this.dataIsSending = false
      }
    },

    applyServerErrors(data) {
      const errors = data?.errors
          || data?.error?.errors
          || null

      if (
          errors
          && typeof errors === 'object'
          && !Array.isArray(errors)
      ) {
        this.serverErrors = errors
        return
      }

      this.generalServerError =
          data?.message
          || this.$t('somethingWentWrongTryAgainMsg')
    },

    close() {
      // if (this.dataIsSending) return
      this.$emit('close')
      this.submitted = false
    },
  },
}
</script>